import { Row } from '@devexpress/dx-react-grid';
import { MainPageContainer, UpperInfoContainer } from 'components/PageLayout';
import { createReportURL } from 'core/routes';
import { convertToDate, useNargControlSheetV5, useSchemeCodes } from 'pages/hooks';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dateToString, formatDate } from 'utils/helpers';
import { WorkProviderScheme } from 'api/resources/models/AutoGenerated';
import { errorMessages } from 'api/resources/responseMessages/errorMessages';
import { AxiosError } from 'axios';
import { ReportsLoader } from 'components/ReportsLoader';
import { NoReportDataMessage } from 'components/ReportsComponents/NoReportDataMessage';
import { ReportGridContainer } from 'components/ReportGridContainer';
import { ReportHeader } from 'components/ReportsComponents/ReportHeader';
import {
  NARG_CONTROL_SHEET_V5_REPORT,
  NARG_CONTROL_SHEET_V5_REPORT_GRID_TITLE,
  reportColumns,
} from './constants';
import { valuesConverter } from './helpers';
import { ReportBackButton } from 'components/ReportsComponents/ReportBackButton';
import { NargSearchForm } from 'components/ReportForms/NargSearchForm';
import { SelectOption } from 'components/types';
import { ReportProps } from 'pages/ReportsDetails/hooks';
import { reportLogger } from 'pages/ReportsDetails/reportLogger';
import ReportGridNew from 'components/ReportGrid_New';
import { noPDFexportFormats } from 'components/ReportGrid_New/constants';

export function NargControlSheetV5({
  reportPath,
  setErrorMessage,
  setToast,
}: Readonly<ReportProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlParamsValue = {
    endDate: urlParams.get('endDate'),
    startDate: urlParams.get('startDate'),
    schemaCodeList: urlParams.get('schemaCodeList'),
  };

  const queryEnabled =
    !!urlParamsValue.startDate &&
    !!urlParamsValue.endDate &&
    !!urlParamsValue.schemaCodeList;

  const { isSchemeCodesLoading, schemeCodes } = useSchemeCodes((error) =>
    handleError(error, 'isSchemeCodes')
  );
  const { reportData, refetchReport, isReportFetching } = useNargControlSheetV5(
    {
      endDate: urlParamsValue.endDate,
      startDate: urlParamsValue.startDate,
      schemaCodeList: urlParamsValue.schemaCodeList,
    },
    queryEnabled,
    (error) => handleError(error, 'isReport')
  );

  const rows = useMemo<Row[] | undefined>(() => reportData ? valuesConverter(reportData) : undefined, [reportData]);

  const [startDate, setStartDate] = useState<Date | null>(
    convertToDate(urlParamsValue.startDate) || null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    convertToDate(urlParamsValue.endDate) || null
  );
  const [schemaCodeList, setSchemaCodeList] = useState<SelectOption[]>([]);
  const options = schemeCodes?.map((e: WorkProviderScheme, i: number) => {
    return { id: i, value: e.schemeCode || '', text: e.schemeCode || '' };
  });
  const isBusy = isReportFetching;
  const isOptionsLoading = isSchemeCodesLoading;

  useEffect(() => {
    navigateIfPossible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    urlParamsValue.startDate,
    urlParamsValue.endDate,
    urlParamsValue.schemaCodeList,
  ]);

  useEffect(() => {
    if (urlParamsValue.schemaCodeList?.length != schemaCodeList?.length)
      setSchemaCodeList(
        options?.filter((e) =>
          urlParamsValue.schemaCodeList?.split(',').includes(e.value)
        ) || []
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOptionsLoading]);

  const reportHeaderTagLine = useMemo(
    () =>
      `Report produced on ${formatDate(
        new Date()
      )} for period ${formatDate(startDate)} to ${formatDate(endDate)}`,
    [startDate, endDate]
  );

  return (
    <MainPageContainer isLoading={isBusy} direction="column" wrap="nowrap">
      <UpperInfoContainer direction="column">
        <ReportBackButton />
        <ReportHeader
          reportName={'NARG Control Sheet V5 Report'}
          infoMessage={
            'Displays the NARG Control Sheet V5 report for the date range provided'
          }
        />
        {isOptionsLoading ? (
          <ReportsLoader loadingRowsCount={2} />
        ) : (
          <NargSearchForm
            handleSubmit={handleSubmit}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            schemaCodeList={schemaCodeList}
            setSchemaCodeList={setSchemaCodeList}
            isDisabled={isBusy}
            options={options}
          />
        )}
      </UpperInfoContainer>

      {!isBusy && rows?.length === 0 && <NoReportDataMessage />}

      <ReportGridContainer direction="column">

        <ReportGridNew
          columns={reportColumns}
          displayGroupingPanel
          exportFormats={noPDFexportFormats}
          headerTitleForSheet={NARG_CONTROL_SHEET_V5_REPORT}
          isLoading={isReportFetching}
          reportHeaderTagLine={reportHeaderTagLine}
          rows={rows}
          title={NARG_CONTROL_SHEET_V5_REPORT_GRID_TITLE}
        />
      </ReportGridContainer>
    </MainPageContainer>
  );

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (
      urlParamsValue.startDate == dateToString(startDate) &&
      urlParamsValue.endDate == dateToString(endDate) &&
      urlParamsValue.schemaCodeList ==
      schemaCodeList?.map((e) => e.value)?.join(',')
    )
      refetchReport();
    else navigateIfPossible();
  }

  function navigateIfPossible() {
    if (!!startDate && !!endDate && schemaCodeList?.length > 0)
      navigate(
        createReportURL(
          {
            startDate: dateToString(startDate),
            endDate: dateToString(endDate),
            schemaCodeList: schemaCodeList?.map((e) => e.value)?.join(','),
          },
          reportPath,
        ), { replace: true });

    else return;
  }

  function handleError(error: AxiosError, type: 'isReport' | 'isSchemeCodes') {
    reportLogger(error);
    setErrorMessage(
      type == 'isReport'
        ? errorMessages.reports('NARG Control Sheet V5 Report')
        : errorMessages.schemeCodes
    );
    setToast(true);
  }
}
